import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Users, Globe } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function PrivacyPolicy() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const styles = {
    container: {
      minHeight: '100vh',
      background: theme.colors.bg.primary,
      padding: '40px 20px',
      color: theme.colors.text.primary
    },
    wrapper: {
      maxWidth: '900px',
      margin: '0 auto',
      background: theme.colors.bg.secondary,
      borderRadius: '20px',
      padding: '40px',
      border: `1px solid ${theme.colors.border.primary}`
    },
    header: {
      marginBottom: '40px'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'transparent',
      border: `1px solid ${theme.colors.border.primary}`,
      borderRadius: '8px',
      padding: '8px 16px',
      color: theme.colors.text.secondary,
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '20px',
      transition: 'all 0.2s'
    },
    title: {
      fontSize: '36px',
      fontWeight: '700',
      marginBottom: '10px',
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    lastUpdated: {
      color: theme.colors.text.tertiary,
      fontSize: '14px'
    },
    section: {
      marginBottom: '32px'
    },
    sectionTitle: {
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: theme.colors.text.primary
    },
    paragraph: {
      fontSize: '16px',
      lineHeight: '1.8',
      color: theme.colors.text.secondary,
      marginBottom: '16px'
    },
    list: {
      listStyle: 'none',
      padding: 0,
      marginBottom: '16px'
    },
    listItem: {
      fontSize: '16px',
      lineHeight: '1.8',
      color: theme.colors.text.secondary,
      marginBottom: '12px',
      paddingLeft: '24px',
      position: 'relative'
    },
    bullet: {
      position: 'absolute',
      left: 0,
      color: '#6366f1'
    },
    highlight: {
      background: 'rgba(99, 102, 241, 0.1)',
      padding: '16px',
      borderRadius: '8px',
      border: `1px solid ${theme.colors.border.primary}`,
      marginBottom: '16px'
    },
    link: {
      color: '#6366f1',
      textDecoration: 'none',
      fontWeight: '500'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <button 
            style={styles.backButton}
            onClick={() => navigate(-1)}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <ArrowLeft size={16} />
            Back
          </button>
          
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.lastUpdated}>Last Updated: February 2, 2026</p>
        </div>

        {/* Introduction */}
        <div style={styles.section}>
          <p style={styles.paragraph}>
            At Bidoryx ("we," "our," or "us"), we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered Google Ads optimization platform.
          </p>
          <p style={styles.paragraph}>
            By using Bidoryx, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>

        {/* Information We Collect */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Database size={24} style={{ color: '#6366f1' }} />
            Information We Collect
          </h2>
          
          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            1. Account Information
          </h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Email address:</strong> Used for account creation and communication
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Name:</strong> To personalize your experience
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Authentication tokens:</strong> Securely stored and encrypted
            </li>
          </ul>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            2. Google Ads Data
          </h3>
          <div style={styles.highlight}>
            <p style={{ ...styles.paragraph, marginBottom: 0, fontSize: '15px' }}>
              <strong>Important:</strong> When you connect your Google Ads account, we request specific permissions to help optimize your campaigns. We only access data necessary for providing our services.
            </p>
          </div>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Campaign data:</strong> Campaign names, budgets, performance metrics
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Ad group data:</strong> Ad groups, keywords, bids, Quality Scores
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Ad performance:</strong> Impressions, clicks, conversions, costs
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Recommendations:</strong> Google Ads API recommendations for optimization
            </li>
          </ul>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            3. Usage Data
          </h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>App usage:</strong> Features used, actions taken, timestamps
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Technical data:</strong> Browser type, IP address, device information
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Performance data:</strong> API response times, error logs (anonymized)
            </li>
          </ul>

          <h3 style={{ ...styles.paragraph, fontWeight: '600', fontSize: '18px', marginTop: '20px' }}>
            4. Payment Information
          </h3>
          <p style={styles.paragraph}>
            Payment processing is handled securely by <strong>Stripe</strong>. We do not store your credit card details. Stripe retains payment information according to their privacy policy.
          </p>
        </div>

        {/* How We Use Your Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Eye size={24} style={{ color: '#10b981' }} />
            How We Use Your Information
          </h2>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Campaign optimization:</strong> Analyze performance and provide AI-powered recommendations
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Bid management:</strong> Automatically adjust bids based on your settings
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Keyword research:</strong> Generate relevant keywords using Google's Keyword Planner API
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Ad creation:</strong> Generate and test ad variations with AI
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Quality Score improvement:</strong> Analyze and suggest improvements for ad relevance
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Service improvement:</strong> Improve our algorithms and user experience
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Communication:</strong> Send important updates, billing notifications, and support responses
            </li>
          </ul>
        </div>

        {/* Google Ads API Scopes */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Globe size={24} style={{ color: '#f59e0b' }} />
            Google Ads API Permissions
          </h2>
          
          <p style={styles.paragraph}>
            Bidoryx requires the following Google Ads API scope to function:
          </p>
          
          <div style={styles.highlight}>
            <p style={{ ...styles.paragraph, marginBottom: '8px', fontFamily: 'monospace', fontSize: '14px' }}>
              <strong>https://www.googleapis.com/auth/adwords</strong>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0, fontSize: '15px' }}>
              This scope allows Bidoryx to:
            </p>
            <ul style={{ ...styles.list, marginTop: '8px' }}>
              <li style={{ ...styles.listItem, fontSize: '14px' }}>
                <span style={styles.bullet}>•</span>
                Read your Google Ads campaign data and performance metrics
              </li>
              <li style={{ ...styles.listItem, fontSize: '14px' }}>
                <span style={styles.bullet}>•</span>
                Create and modify campaigns, ad groups, keywords, and ads
              </li>
              <li style={{ ...styles.listItem, fontSize: '14px' }}>
                <span style={styles.bullet}>•</span>
                Access keyword research data via Keyword Planner
              </li>
              <li style={{ ...styles.listItem, fontSize: '14px' }}>
                <span style={styles.bullet}>•</span>
                Apply optimization recommendations and adjust bids
              </li>
            </ul>
          </div>

          <p style={styles.paragraph}>
            <strong>You maintain full control:</strong> You can revoke Bidoryx's access to your Google Ads account at any time through your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" style={styles.link}>Google Account Permissions</a> page.
          </p>
        </div>

        {/* Data Storage and Security */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Lock size={24} style={{ color: '#ef4444' }} />
            Data Storage and Security
          </h2>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest (AES-256)
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Access tokens:</strong> Google OAuth tokens are encrypted and stored securely
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Database:</strong> PostgreSQL hosted on secure infrastructure with regular backups
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Access control:</strong> Strict internal access policies and audit logs
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Data retention:</strong> We retain your data as long as your account is active
            </li>
          </ul>
        </div>

        {/* Data Sharing */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Users size={24} style={{ color: '#8b5cf6' }} />
            Data Sharing and Third Parties
          </h2>
          
          <p style={styles.paragraph}>
            <strong>We do NOT sell your data.</strong> We only share data with the following trusted service providers:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Google Ads API:</strong> To read and manage your campaigns (with your explicit permission)
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>OpenAI:</strong> To generate AI-powered ad copy and keyword suggestions (anonymized data only)
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Stripe:</strong> For secure payment processing
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Clerk:</strong> For authentication and user management
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Railway:</strong> Infrastructure and hosting provider
            </li>
          </ul>

          <p style={styles.paragraph}>
            All third-party service providers are contractually obligated to protect your data and use it only for the specified services.
          </p>
        </div>

        {/* Your Rights (GDPR) */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <Shield size={24} style={{ color: '#06b6d4' }} />
            Your Rights (GDPR Compliance)
          </h2>
          
          <p style={styles.paragraph}>
            If you are located in the European Economic Area (EEA), you have the following rights:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to access:</strong> Request a copy of your personal data
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to rectification:</strong> Correct inaccurate or incomplete data
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to erasure:</strong> Request deletion of your data ("right to be forgotten")
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to restrict processing:</strong> Limit how we use your data
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to data portability:</strong> Receive your data in a machine-readable format
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              <strong>Right to object:</strong> Object to certain types of data processing
            </li>
          </ul>

          <p style={styles.paragraph}>
            To exercise any of these rights, please contact us at <a href="mailto:privacy@bidoryx.com" style={styles.link}>privacy@bidoryx.com</a>
          </p>
        </div>

        {/* Cookies and Tracking */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Cookies and Tracking
          </h2>
          
          <p style={styles.paragraph}>
            Bidoryx uses cookies and similar tracking technologies to:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Maintain your logged-in session
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Remember your preferences and settings
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Analyze app usage and performance
            </li>
          </ul>

          <p style={styles.paragraph}>
            You can control cookies through your browser settings. Note that disabling cookies may affect app functionality.
          </p>
        </div>

        {/* Children's Privacy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Children's Privacy
          </h2>
          
          <p style={styles.paragraph}>
            Bidoryx is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected data from a child, please contact us immediately.
          </p>
        </div>

        {/* Changes to Privacy Policy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Changes to This Privacy Policy
          </h2>
          
          <p style={styles.paragraph}>
            We may update this Privacy Policy from time to time. We will notify you of any significant changes by:
          </p>
          
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Updating the "Last Updated" date at the top of this page
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Sending an email notification to your registered email address
            </li>
            <li style={styles.listItem}>
              <span style={styles.bullet}>•</span>
              Displaying a prominent notice in the app
            </li>
          </ul>

          <p style={styles.paragraph}>
            Continued use of Bidoryx after changes constitutes acceptance of the updated Privacy Policy.
          </p>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Contact Us
          </h2>
          
          <p style={styles.paragraph}>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
          </p>
          
          <div style={styles.highlight}>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong>Email:</strong> <a href="mailto:privacy@bidoryx.com" style={styles.link}>privacy@bidoryx.com</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: '8px' }}>
              <strong>Support:</strong> <a href="mailto:support@bidoryx.com" style={styles.link}>support@bidoryx.com</a>
            </p>
            <p style={{ ...styles.paragraph, marginBottom: 0 }}>
              <strong>Website:</strong> <a href="https://app.bidoryx.com" style={styles.link}>https://app.bidoryx.com</a>
            </p>
          </div>
        </div>

        {/* Data Protection Officer */}
        <div style={styles.section}>
          <p style={{ ...styles.paragraph, fontSize: '14px', color: theme.colors.text.tertiary, marginBottom: 0 }}>
            For EU residents: If you have concerns about how we handle your data, you have the right to lodge a complaint with your local data protection authority.
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
